import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { cn } from '@/lib/utils/cn'
import type { VenueCampaignRecipientItemView } from '@/lib/venue/campaignTypes'
import { RECIPIENT_READ_FILTER_OPTIONS, type RecipientReadFilter } from '@/lib/venue/campaignViewModel'
import { EMPTY_MARK, formatDateTime } from '@/lib/venue/format'

/**
 * 대상별 읽음 목록(#728 §6, 요구사항 "대상별 읽음 목록").
 *
 * 🚨🚨 `read=false`(안읽음) 필터에는 발송 실패자가 섞여 있다 — `sendResult`로 반드시 구분해서
 * 그린다("다시 보내면 되겠다"는 오판을 막는다).
 */
export function NoticeCampaignRecipientListTable({
  items,
  readFilter,
  onReadFilterChange,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  items: VenueCampaignRecipientItemView[]
  readFilter: RecipientReadFilter
  onReadFilterChange: (filter: RecipientReadFilter) => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  return (
    <div className="space-y-3">
      <div role="tablist" aria-label="읽음 여부 필터" className="flex gap-2">
        {RECIPIENT_READ_FILTER_OPTIONS.map((option) => {
          const selected = readFilter === option.value
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => {
                onReadFilterChange(option.value)
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {items.length === 0 ? (
        <EmptyState title="조회된 대상이 없습니다" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slateish-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slateish-200 bg-slateish-50 text-left text-xs font-medium text-slateish-600">
                <th className="px-3 py-2">대상</th>
                <th className="px-3 py-2">발송 결과</th>
                <th className="px-3 py-2">발송 시각</th>
                <th className="px-3 py-2">읽음</th>
                <th className="px-3 py-2">읽은 시각</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.memberId} className="border-b border-slateish-200 last:border-b-0">
                  <td className="px-3 py-2">{item.maskedName ?? '(탈퇴 회원)'}</td>
                  <td className="px-3 py-2">
                    <Badge tone={item.sendResult === 'SUCCESS' ? 'success' : 'danger'}>{item.sendResultLabel}</Badge>
                  </td>
                  <td className="px-3 py-2">{formatDateTime(item.sentAt)}</td>
                  <td className="px-3 py-2">{item.read ? '읽음' : '안읽음'}</td>
                  <td className="px-3 py-2">{item.readAt ? formatDateTime(item.readAt) : EMPTY_MARK}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="secondary" size="sm" disabled={isFetchingNextPage} onClick={onLoadMore}>
            {isFetchingNextPage ? '불러오는 중' : '더 보기'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
